import { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FileText, BarChart3, Download, Calendar, Filter,
    CheckCircle, AlertCircle, Loader2, FileSpreadsheet,
    Globe, Code2, Eye, EyeOff, Table2, X,
} from 'lucide-react';
// ui imports are available if needed in future extensions
import { ServiceDefinition, Department } from '../types';

interface ExportRequestParams {
    start_date?: string;
    end_date?: string;
    status?: string;
    service_code?: string;
    department_id?: string;
    priority?: string;
    merged_only?: boolean;
    include_pii?: boolean;
}

interface ReportsPanelProps {
    onExportRequests: (format: 'csv' | 'json' | 'geojson', params?: ExportRequestParams) => Promise<void>;
    onExportStatistics: (format: 'csv' | 'json', params?: { start_date?: string; end_date?: string }) => Promise<void>;
    onPreviewRequests?: (params: ExportRequestParams) => Promise<any[]>;
    onPreviewStatistics?: (params: { start_date?: string; end_date?: string }) => Promise<any>;
    services: ServiceDefinition[];
    departments: Department[];
}

type ReportType = 'requests' | 'statistics';

const PRESET_RANGES = [
    { label: 'Today', getValue: () => { const d = new Date().toISOString().split('T')[0]; return { start: d, end: d }; } },
    { label: 'Last 7 days', getValue: () => { const e = new Date(); const s = new Date(e); s.setDate(s.getDate() - 7); return { start: s.toISOString().split('T')[0], end: e.toISOString().split('T')[0] }; } },
    { label: 'This month', getValue: () => { const n = new Date(); return { start: new Date(n.getFullYear(), n.getMonth(), 1).toISOString().split('T')[0], end: n.toISOString().split('T')[0] }; } },
    { label: 'Last month', getValue: () => { const n = new Date(); const s = new Date(n.getFullYear(), n.getMonth() - 1, 1); const e = new Date(n.getFullYear(), n.getMonth(), 0); return { start: s.toISOString().split('T')[0], end: e.toISOString().split('T')[0] }; } },
    { label: 'Last 3 months', getValue: () => { const e = new Date(); const s = new Date(e); s.setMonth(s.getMonth() - 3); return { start: s.toISOString().split('T')[0], end: e.toISOString().split('T')[0] }; } },
    { label: 'This year', getValue: () => { const n = new Date(); return { start: new Date(n.getFullYear(), 0, 1).toISOString().split('T')[0], end: n.toISOString().split('T')[0] }; } },
    { label: 'All time', getValue: () => ({ start: '', end: '' }) },
];

export default function ReportsPanel({ onExportRequests, onExportStatistics, onPreviewRequests, onPreviewStatistics, services, departments }: ReportsPanelProps) {
    const [reportType, setReportType] = useState<ReportType>('requests');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [status, setStatus] = useState('');
    const [serviceCode, setServiceCode] = useState('');
    const [departmentId, setDepartmentId] = useState('');
    const [priority, setPriority] = useState('');
    const [mergedOnly, setMergedOnly] = useState(false);
    const [format, setFormat] = useState<'csv' | 'json' | 'geojson'>('csv');
    const [statsFormat, setStatsFormat] = useState<'csv' | 'json'>('csv');
    const [includePii, setIncludePii] = useState(true);
    const [loading, setLoading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [lastResult, setLastResult] = useState<{ success: boolean; message: string } | null>(null);
    const [previewData, setPreviewData] = useState<any[] | null>(null);
    const [previewStats, setPreviewStats] = useState<any | null>(null);

    const applyPreset = (preset: typeof PRESET_RANGES[0]) => {
        const { start, end } = preset.getValue();
        setStartDate(start);
        setEndDate(end);
    };

    const buildRequestParams = () => ({
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        status: status || undefined,
        service_code: serviceCode || undefined,
        department_id: departmentId || undefined,
        priority: priority || undefined,
        merged_only: mergedOnly || undefined,
        include_pii: includePii,
    });

    const handleGenerate = async () => {
        setLoading(true);
        setLastResult(null);
        try {
            if (reportType === 'requests') {
                await onExportRequests(format, buildRequestParams());
            } else {
                await onExportStatistics(statsFormat, {
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                });
            }
            setLastResult({ success: true, message: 'Report downloaded successfully.' });
        } catch (err: unknown) {
            setLastResult({ success: false, message: err instanceof Error ? err.message : 'Export failed' });
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = async () => {
        setPreviewLoading(true);
        setLastResult(null);
        setPreviewData(null);
        setPreviewStats(null);
        try {
            if (reportType === 'requests' && onPreviewRequests) {
                const data = await onPreviewRequests(buildRequestParams());
                setPreviewData(data);
            } else if (reportType === 'statistics' && onPreviewStatistics) {
                const data = await onPreviewStatistics({
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                });
                setPreviewStats(data);
            }
        } catch (err: unknown) {
            setLastResult({ success: false, message: err instanceof Error ? err.message : 'Preview failed' });
        } finally {
            setPreviewLoading(false);
        }
    };

    const formats_requests = [
        { value: 'csv', label: 'CSV — spreadsheet (Excel, Sheets)', icon: FileSpreadsheet },
        { value: 'json', label: 'JSON — structured data', icon: Code2 },
        { value: 'geojson', label: 'GeoJSON — map data', icon: Globe },
    ] as const;

    const formats_stats = [
        { value: 'csv', label: 'CSV — spreadsheet', icon: FileSpreadsheet },
        { value: 'json', label: 'JSON — structured data', icon: Code2 },
    ] as const;

    return (
        <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
            <div className="max-w-3xl mx-auto space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-white">Report Generation</h1>
                    <p className="text-white/50 text-sm mt-1">Export service request data and statistics as downloadable files.</p>
                </div>

                {/* Report Type selector */}
                <div className="grid grid-cols-2 gap-3">
                    {([
                        { id: 'requests', icon: FileText, label: 'Service Requests', desc: 'Raw request data with filters' },
                        { id: 'statistics', icon: BarChart3, label: 'Statistics Summary', desc: 'Aggregated counts & metrics' },
                    ] as const).map(opt => (
                        <motion.button
                            key={opt.id}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setReportType(opt.id)}
                            className={`flex flex-col items-start gap-2 p-4 rounded-xl border transition-all text-left ${reportType === opt.id
                                ? 'bg-primary-500/15 border-primary-500/40 shadow-lg shadow-primary-500/10'
                                : 'bg-white/5 border-white/10 hover:bg-white/8 hover:border-white/20'
                            }`}
                        >
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${reportType === opt.id ? 'bg-primary-500/20' : 'bg-white/10'}`}>
                                <opt.icon className={`w-5 h-5 ${reportType === opt.id ? 'text-primary-400' : 'text-white/60'}`} />
                            </div>
                            <div>
                                <p className={`font-semibold text-sm ${reportType === opt.id ? 'text-white' : 'text-white/70'}`}>{opt.label}</p>
                                <p className="text-xs text-white/40">{opt.desc}</p>
                            </div>
                        </motion.button>
                    ))}
                </div>

                {/* Date Range */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-white/80">
                        <Calendar className="w-4 h-4 text-primary-400" />
                        Date Range
                    </div>

                    {/* Presets */}
                    <div className="flex flex-wrap gap-2">
                        {PRESET_RANGES.map(p => (
                            <button
                                key={p.label}
                                onClick={() => applyPreset(p)}
                                className="px-3 py-1 rounded-lg bg-white/8 border border-white/10 text-xs text-white/60 hover:bg-white/15 hover:text-white transition-colors"
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-white/50 mb-1 block">From</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none focus:border-primary-500/60 [color-scheme:dark]"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-white/50 mb-1 block">To</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none focus:border-primary-500/60 [color-scheme:dark]"
                            />
                        </div>
                    </div>
                </div>

                {/* Filters — only for requests */}
                {reportType === 'requests' && (
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
                        <div className="flex items-center gap-2 text-sm font-semibold text-white/80">
                            <Filter className="w-4 h-4 text-primary-400" />
                            Filters
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-white/50 mb-1 block">Status</label>
                                <select
                                    value={status}
                                    onChange={e => setStatus(e.target.value)}
                                    className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none focus:border-primary-500/60"
                                >
                                    <option value="">All statuses</option>
                                    <option value="open">Open</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="closed">Closed</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-white/50 mb-1 block">Priority</label>
                                <select
                                    value={priority}
                                    onChange={e => setPriority(e.target.value)}
                                    className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none focus:border-primary-500/60"
                                >
                                    <option value="">All priorities</option>
                                    <option value="critical">Critical</option>
                                    <option value="high">High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-white/50 mb-1 block">Service type</label>
                                <select
                                    value={serviceCode}
                                    onChange={e => setServiceCode(e.target.value)}
                                    className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none focus:border-primary-500/60"
                                >
                                    <option value="">All services</option>
                                    {services.map(s => (
                                        <option key={s.service_code} value={s.service_code}>{s.service_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-white/50 mb-1 block">Department</label>
                                <select
                                    value={departmentId}
                                    onChange={e => setDepartmentId(e.target.value)}
                                    className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:outline-none focus:border-primary-500/60"
                                >
                                    <option value="">All departments</option>
                                    {departments.map(d => (
                                        <option key={d.id} value={String(d.id)}>{d.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Merged tickets toggle */}
                        <button
                            onClick={() => setMergedOnly(m => !m)}
                            className={`flex items-center gap-3 w-full p-3 rounded-xl border transition-colors ${mergedOnly ? 'bg-purple-500/10 border-purple-500/30' : 'bg-white/5 border-white/10'}`}
                        >
                            <div className={`w-4 h-4 flex-shrink-0 rounded-full border-2 flex items-center justify-center ${mergedOnly ? 'border-purple-400 bg-purple-500/30' : 'border-white/30'}`}>
                                {mergedOnly && <div className="w-2 h-2 rounded-full bg-purple-400" />}
                            </div>
                            <div className="text-left">
                                <p className="text-sm font-medium text-white">Merged tickets only</p>
                                <p className="text-xs text-white/40">Show only tickets that were merged into another</p>
                            </div>
                            <div className={`ml-auto w-9 h-5 rounded-full transition-colors ${mergedOnly ? 'bg-purple-500' : 'bg-white/20'} flex-shrink-0 relative`}>
                                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${mergedOnly ? 'translate-x-4' : 'translate-x-0.5'}`} />
                            </div>
                        </button>

                        {/* Include PII toggle */}
                        <button
                            onClick={() => setIncludePii(p => !p)}
                            className={`flex items-center gap-3 w-full p-3 rounded-xl border transition-colors ${includePii ? 'bg-amber-500/10 border-amber-500/30' : 'bg-white/5 border-white/10'}`}
                        >
                            {includePii
                                ? <Eye className="w-4 h-4 text-amber-400 flex-shrink-0" />
                                : <EyeOff className="w-4 h-4 text-white/40 flex-shrink-0" />
                            }
                            <div className="text-left">
                                <p className="text-sm font-medium text-white">{includePii ? 'Include reporter info (PII)' : 'Redact reporter info (PII)'}</p>
                                <p className="text-xs text-white/40">{includePii ? 'Name, email, phone included in export' : 'Reporter details will be removed'}</p>
                            </div>
                            <div className={`ml-auto w-9 h-5 rounded-full transition-colors ${includePii ? 'bg-amber-500' : 'bg-white/20'} flex-shrink-0 relative`}>
                                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${includePii ? 'translate-x-4' : 'translate-x-0.5'}`} />
                            </div>
                        </button>
                    </div>
                )}

                {/* Format */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-white/80">
                        <Download className="w-4 h-4 text-primary-400" />
                        Output Format
                    </div>
                    <div className="grid gap-2">
                        {(reportType === 'requests' ? formats_requests : formats_stats).map(f => (
                            <button
                                key={f.value}
                                onClick={() => reportType === 'requests' ? setFormat(f.value as any) : setStatsFormat(f.value as any)}
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                                    (reportType === 'requests' ? format : statsFormat) === f.value
                                        ? 'bg-primary-500/15 border-primary-500/40 text-white'
                                        : 'bg-white/5 border-white/10 text-white/60 hover:text-white hover:bg-white/8'
                                }`}
                            >
                                <f.icon className="w-4 h-4 flex-shrink-0" />
                                <span className="text-sm">{f.label}</span>
                                {(reportType === 'requests' ? format : statsFormat) === f.value && (
                                    <CheckCircle className="w-4 h-4 text-primary-400 ml-auto flex-shrink-0" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Generate buttons */}
                <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                            onClick={handlePreview}
                            disabled={previewLoading || loading}
                            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold text-sm transition-all disabled:opacity-60"
                        >
                            {previewLoading
                                ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</>
                                : <><Table2 className="w-4 h-4" /> Preview in browser</>
                            }
                        </button>
                        <button
                            onClick={handleGenerate}
                            disabled={loading || previewLoading}
                            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-primary-500 to-teal-500 hover:from-primary-600 hover:to-teal-600 text-white font-semibold text-sm transition-all shadow-lg shadow-primary-500/20 disabled:opacity-60"
                        >
                            {loading
                                ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                                : <><Download className="w-4 h-4" /> Download Report</>
                            }
                        </button>
                    </div>

                    {lastResult && (
                        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm ${lastResult.success ? 'bg-green-500/15 border border-green-500/30 text-green-300' : 'bg-red-500/15 border border-red-500/30 text-red-300'}`}>
                            {lastResult.success ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
                            {lastResult.message}
                        </div>
                    )}
                </div>

                {/* Preview results */}
                {previewData && (
                    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-white/5">
                            <div className="flex items-center gap-2 text-sm font-semibold text-white">
                                <Table2 className="w-4 h-4 text-primary-400" />
                                Preview — {previewData.length} request{previewData.length !== 1 ? 's' : ''}
                            </div>
                            <button onClick={() => setPreviewData(null)} className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        {previewData.length === 0 ? (
                            <div className="p-8 text-center text-white/50 text-sm">No requests match the selected filters.</div>
                        ) : (
                            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                                <table className="w-full text-xs">
                                    <thead className="bg-white/5 sticky top-0 backdrop-blur-sm">
                                        <tr className="text-left text-white/70">
                                            <th className="px-3 py-2 font-semibold">Request ID</th>
                                            <th className="px-3 py-2 font-semibold">Status</th>
                                            <th className="px-3 py-2 font-semibold">Priority</th>
                                            <th className="px-3 py-2 font-semibold">Service</th>
                                            <th className="px-3 py-2 font-semibold">Department</th>
                                            <th className="px-3 py-2 font-semibold">Address</th>
                                            <th className="px-3 py-2 font-semibold">Created</th>
                                            <th className="px-3 py-2 font-semibold">Merged Into</th>
                                            {includePii && <th className="px-3 py-2 font-semibold">Reporter</th>}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                        {previewData.slice(0, 200).map((r, i) => (
                                            <tr key={r.id || i} className={`text-white/80 hover:bg-white/5 ${r.merged_into_id ? 'opacity-60' : ''}`}>
                                                <td className="px-3 py-2 font-mono text-[11px]">{r.request_id || r.id || '—'}</td>
                                                <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full bg-white/10 text-white/80 text-[10px]">{r.status || '—'}</span></td>
                                                <td className="px-3 py-2">
                                                    {r.priority ? (
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${r.priority === 'critical' ? 'bg-red-500/20 text-red-300' : r.priority === 'high' ? 'bg-orange-500/20 text-orange-300' : r.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-300' : 'bg-blue-500/20 text-blue-300'}`}>{r.priority}</span>
                                                    ) : '—'}
                                                </td>
                                                <td className="px-3 py-2">{r.service_name || r.service_code || '—'}</td>
                                                <td className="px-3 py-2">{r.department_name || '—'}</td>
                                                <td className="px-3 py-2 max-w-[200px] truncate">{r.address || '—'}</td>
                                                <td className="px-3 py-2 whitespace-nowrap">{r.created_at || '—'}</td>
                                                <td className="px-3 py-2">
                                                    {r.merged_into_id ? (
                                                        <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-[10px] font-medium">→ #{r.merged_into_id}</span>
                                                    ) : '—'}
                                                </td>
                                                {includePii && <td className="px-3 py-2">{r.reporter_name || '—'}</td>}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {previewData.length > 200 && (
                                    <div className="px-4 py-2 text-xs text-white/50 bg-white/5 border-t border-white/10">
                                        Showing first 200 of {previewData.length}. Download the report to see all rows.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {previewStats && (
                    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-white/5">
                            <div className="flex items-center gap-2 text-sm font-semibold text-white">
                                <BarChart3 className="w-4 h-4 text-primary-400" />
                                Statistics Preview
                            </div>
                            <button onClick={() => setPreviewStats(null)} className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            {/* Summary */}
                            {previewStats.summary && (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    {[
                                        { label: 'Total Requests', value: previewStats.summary.total_requests ?? 0, color: 'text-white' },
                                        { label: 'Resolved', value: previewStats.summary.resolved_count ?? 0, color: 'text-green-300' },
                                        { label: 'Avg Resolution', value: `${previewStats.summary.average_resolution_hours ?? 0}h`, color: 'text-blue-300' },
                                        { label: 'Merged Tickets', value: previewStats.summary.merged_count ?? 0, color: 'text-purple-300' },
                                    ].map(stat => (
                                        <div key={stat.label} className="bg-white/5 rounded-xl p-3 text-center">
                                            <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
                                            <p className="text-[11px] text-white/50 mt-0.5">{stat.label}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {/* Breakdowns */}
                            {(['by_status', 'by_priority', 'by_category', 'by_department'] as const).map(key => {
                                const section = previewStats[key];
                                if (!section || !Object.keys(section).length) return null;
                                const total = Object.values(section).reduce((a: number, b) => a + (b as number), 0);
                                return (
                                    <div key={key}>
                                        <p className="text-xs uppercase tracking-wide text-white/50 font-semibold mb-2">{key.replace(/_/g, ' ')}</p>
                                        <div className="space-y-1.5">
                                            {Object.entries(section).sort(([, a], [, b]) => (b as number) - (a as number)).map(([k, v]) => (
                                                <div key={k} className="flex items-center gap-2">
                                                    <span className="text-xs text-white/70 w-28 truncate flex-shrink-0 capitalize">{k}</span>
                                                    <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                                                        <div className="h-full bg-primary-500/60 rounded-full" style={{ width: `${Math.round(((v as number) / (total as number)) * 100)}%` }} />
                                                    </div>
                                                    <span className="text-xs text-white/70 w-8 text-right flex-shrink-0">{v as number}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
